// ─── Telegram → Segnalazioni ─────────────────────────────────────────────────
//
// 💡 24/09/2026, dal mockup approvato. 🗣️ *«è importante ricevere dei
// suggerimenti dalle persone, si impara sempre molto»*. Il socio li scrive dal
// bot (💡 Suggerimenti e problemi); qui la segreteria li legge, li segna come
// letti e risponde. La risposta arriva al socio su Telegram.
//
// ⚖️ Il 24/09 prese nel menu il posto di «Avvisi»; dal 25/09 ci stanno tutte e
// due (⇒ `App.tsx`).
//
// 🤖 26/09/2026, dal mockup approvato: ci scrive anche l'ASSISTENTE DIGITALE,
// quando trova qualcosa che non torna (la prima: l'ora libera che il listino
// non sa quotare). L'anomalia non ha un socio, quindi non ha «Rispondi»: ha
// «Metti il prezzo che manca», che porta nel listino sulla casella vuota.
//
// ✨ 26/09/2026, dal mockup approvato: «Consulta AI» accanto a «Rispondi», sui
// problemi. La proposta dell'AI NON parte: «Usa questa risposta» la mette nel
// riquadro, dove si corregge, e parte solo con «Manda la risposta». Dopo, l'AI
// propone la regola da aggiungere in «Cosa dice il bot ai soci».
//
// 🤖 27/09/2026 (voce 31, passo 2, mockup approvato): ci scrive anche il BOT, per
// un socio — la domanda a cui l'assistente non ha saputo rispondere («Senza
// risposta») e la prenotazione rimasta senza verdetto («Senza esito»). Si
// risponde come agli altri messaggi; la scheda la firma l'assistente, e per la
// prenotazione il gestionale dice se c'è.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ETICHETTE_TIPO_SUGGERIMENTO,
  FILTRI_SUGGERIMENTI,
  scrittoDalBot,
  type ElencoSuggerimenti,
  type PropostaAi,
  type RegolaAi,
  type StatoAi,
  type Suggerimento,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

export const CHIAVE_SUGGERIMENTI = 'suggerimenti';

type Filtro = (typeof FILTRI_SUGGERIMENTI)[number];
const ETICHETTE_FILTRO: Record<Filtro, string> = { da_leggere: 'Da leggere', letti: 'Letti', tutti: 'Tutti' };

/** Il numero accanto alla voce del menu. Si rilegge ogni due minuti: un problema non deve aspettare un ricarico. */
export function usaDaLeggere(abilitato: boolean): number {
  const q = useQuery({
    queryKey: [CHIAVE_SUGGERIMENTI, 'conto'],
    queryFn: () => chiama<{ daLeggere: number }>('GET', '/feedback/count'),
    enabled: abilitato,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  return q.data?.daLeggere ?? 0;
}

/**
 * 🟢 Se «Consulta AI» funziona (27/09/2026, mockup approvato, strada B). Il
 * gestionale lo controlla al massimo ogni 10 minuti, senza consultare niente.
 */
const CHIAVE_STATO_AI = 'consulta-ai-stato';
function usaStatoAi(): StatoAi | undefined {
  return useQuery({
    queryKey: [CHIAVE_STATO_AI],
    queryFn: () => chiama<StatoAi>('GET', '/feedback/ai-status'),
    staleTime: 60_000,
  }).data;
}

const alle = (iso: string) =>
  new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

const RIGA_AI: Record<StatoAi['stato'], { classe: string; titolo: string; perché: string }> = {
  collegata: { classe: 'si', titolo: '✨ Consulta AI: collegata.', perché: '' },
  non_risponde: {
    classe: 'forse',
    titolo: '✨ Consulta AI: la chiave c’è, ma l’AI non risponde.',
    perché: 'Può essere sbagliata, scaduta o senza credito: va guardata da chi gestisce il server.',
  },
  non_collegata: {
    classe: 'no',
    titolo: '✨ Consulta AI: non collegata.',
    perché: 'Sul server manca la chiave. Si risponde a mano, come sempre.',
  },
};

function RigaAi({ s }: { s: StatoAi }): React.JSX.Element {
  const r = RIGA_AI[s.stato];
  const ora = s.controllatoAlle ? `Controllato alle ${alle(s.controllatoAlle)}.` : '';
  return (
    <div className={`stato-ai ${r.classe}`} role="status">
      <span className="punto" aria-hidden="true" />
      <span>
        <strong>{r.titolo}</strong> <span className="tenue">{[r.perché, ora].filter(Boolean).join(' ')}</span>
      </span>
    </div>
  );
}

/** ✉️ «aspetta da 25 min», «da 2 ore», «da 1 giorno»: quanto aspetta un messaggio senza risposta. */
export function daQuanto(iso: string, adesso = Date.now()): string {
  const min = Math.max(0, Math.floor((adesso - new Date(iso).getTime()) / 60_000));
  if (min < 1) return 'appena arrivato';
  if (min < 60) return `aspetta da ${min} min`;
  const ore = Math.floor(min / 60);
  if (ore < 24) return `aspetta da ${ore} ${ore === 1 ? 'ora' : 'ore'}`;
  const giorni = Math.floor(ore / 24);
  return `aspetta da ${giorni} ${giorni === 1 ? 'giorno' : 'giorni'}`;
}

function quando(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Suggerimenti(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const [filtro, setFiltro] = useState<Filtro>('da_leggere');
  /**
   * ✨ Le segnalazioni con la regola dell'AI ancora da decidere restano in
   * pagina: rispondendo diventano «lette», e nella linguetta «Da leggere»
   * sparirebbero col riquadro della regola.
   */
  const [trattenute, setTrattenute] = useState<Suggerimento[]>([]);
  const trattieni = (x: Suggerimento) => setTrattenute((t) => [...t.filter((y) => y.id !== x.id), x]);
  const rilascia = (id: string) => setTrattenute((t) => t.filter((y) => y.id !== id));
  const statoAi = usaStatoAi();
  const elenco = useQuery({
    queryKey: [CHIAVE_SUGGERIMENTI, filtro],
    queryFn: () => chiama<ElencoSuggerimenti>('GET', `/feedback?filtro=${filtro}`),
    placeholderData: (prima) => prima,
  });

  return (
    <section className="pagina-suggerimenti">
      <header className="testata">
        <h2>Segnalazioni</h2>
        <p className="tenue">
          Le domande, i problemi e i suggerimenti che i soci scrivono dal bot, le domande a cui l’assistente
          non ha saputo rispondere, le richieste finite senza esito e le anomalie che trova l’assistente
          digitale. Si risponde da qui: la risposta arriva al socio nella chat del bot. Un problema avvisa
          anche lo staff su Telegram.
        </p>
        <SoloLettura mostra={modificabile} />
        {statoAi && <RigaAi s={statoAi} />}
      </header>

      <div className="linguette-campi" role="group" aria-label="Quali mostrare">
        {FILTRI_SUGGERIMENTI.map((f) => (
          <button
            key={f}
            type="button"
            className={f === filtro ? 'scelta' : ''}
            aria-pressed={f === filtro}
            onClick={() => setFiltro(f)}
          >
            {ETICHETTE_FILTRO[f]}
            {f === 'da_leggere' && elenco.data ? ` · ${elenco.data.daLeggere}` : ''}
          </button>
        ))}
      </div>

      {elenco.isLoading ? (
        <Attesa cosa="i suggerimenti" />
      ) : elenco.error ? (
        <Guasto errore={elenco.error} />
      ) : voci(elenco.data?.items ?? [], trattenute).length === 0 ? (
        <Vuoto
          titolo={filtro === 'da_leggere' ? 'Niente da leggere' : 'Ancora nessun messaggio'}
          perché={
            filtro === 'da_leggere'
              ? 'Le segnalazioni le avete viste tutte.'
              : 'Quando un socio scrive dal bot, o l’assistente digitale trova qualcosa che non torna, compare qui.'
          }
        />
      ) : (
        <div className="riquadro elenco">
          {voci(elenco.data!.items, trattenute).map((s) => (
            <Voce key={s.id} s={s} modificabile={modificabile} trattieni={trattieni} rilascia={rilascia} />
          ))}
        </div>
      )}
    </section>
  );
}

/** L'elenco del server, più le trattenute che non ci sono più: prima chi gioca oggi, poi dalla più recente. */
function voci(dalServer: readonly Suggerimento[], trattenute: readonly Suggerimento[]): Suggerimento[] {
  const ci = new Set(dalServer.map((x) => x.id));
  // ✉️ Prima chi gioca oggi e aspetta ancora una risposta (come l'ordine del gestionale), poi i più recenti.
  const urgente = (x: Suggerimento): number => (x.giocaOggi && !x.reply ? 1 : 0);
  return [...dalServer, ...trattenute.filter((x) => !ci.has(x.id))].sort(
    (a, b) => urgente(b) - urgente(a) || b.createdAt.localeCompare(a.createdAt),
  );
}

interface PropsVoce {
  s: Suggerimento;
  modificabile: boolean;
  trattieni: (s: Suggerimento) => void;
  rilascia: (id: string) => void;
}

function Voce(p: PropsVoce): React.JSX.Element {
  if (p.s.kind === 'anomalia') return <Anomalia s={p.s} modificabile={p.modificabile} />;
  return <DelSocio {...p} />;
}

/**
 * 🤖 La segnalazione dell'assistente digitale: firmata da lui, senza link a un
 * socio e senza «Rispondi». Il bottone porta dove si rimedia.
 */
function Anomalia({ s, modificabile }: { s: Suggerimento; modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE_SUGGERIMENTI);
  const vai = useNavigate();
  const letta = useMutation({
    meta: { fatto: 'segnata come letta' },
    mutationFn: () => chiama('POST', `/feedback/${s.id}/read`),
    onSuccess: rileggi,
  });
  const c = s.missingCell;
  const dove = c
    ? `/impostazioni/listino?listino=${c.listinoId}&tabella=${c.tabellaId}&giorno=${c.weekday}&ora=${c.time}`
    : '/impostazioni/listino';
  return (
    <article className="voce anomalia">
      <div className="testa">
        <span className="tipo anomalia">{ETICHETTE_TIPO_SUGGERIMENTO.anomalia}</span>
        <span className="chi">🤖 Assistente digitale</span>
        <span className="tenue piccolo">
          {quando(s.createdAt)}
          {s.readAt && ' · letta'}
        </span>
      </div>
      <p className="testo">{s.text}</p>
      {modificabile && (
        <div className="azioni">
          <button type="button" onClick={() => void vai(dove)}>
            Metti il prezzo che manca
          </button>
          {!s.readAt && (
            <button
              type="button"
              className="bordato"
              disabled={letta.isPending}
              onClick={() => letta.mutate()}
            >
              Segna come letta
            </button>
          )}
        </div>
      )}
      <Guasto errore={letta.error} />
    </article>
  );
}

function DelSocio({ s, modificabile, trattieni, rilascia }: PropsVoce): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE_SUGGERIMENTI);
  const rileggiIlBot = usaRilettura('conoscenza-bot');
  const rileggiStatoAi = usaRilettura(CHIAVE_STATO_AI);
  // Strada B: se l'AI non funziona il bottone è spento. Finché lo stato non è letto, resta com'era.
  const statoAi = usaStatoAi();
  const aiSpenta = statoAi !== undefined && statoAi.stato !== 'collegata';
  const [scrive, setScrive] = useState(false);
  const [bozza, setBozza] = useState('');
  const nome = (s.memberName ?? '').split(' ')[0];
  /** ✨ La proposta dell'AI, se è stata chiesta; e il testo usato, se è stato usato. */
  const [proposta, setProposta] = useState<PropostaAi | null>(null);
  const [usata, setUsata] = useState<string | null>(null);
  const [regola, setRegola] = useState<RegolaAi | null>(null);
  const [testoRegola, setTestoRegola] = useState('');
  const [regolaFatta, setRegolaFatta] = useState<string | null>(null);

  const letto = useMutation({
    meta: { fatto: 'segnato come letto' },
    mutationFn: () => chiama('POST', `/feedback/${s.id}/read`),
    onSuccess: rileggi,
  });
  // ✨ Consultare non cambia niente: nessun «Fatto», la risposta sta nel riquadro.
  const consulta = useMutation({
    meta: { fatto: () => null },
    mutationFn: () => chiama<PropostaAi>('POST', `/feedback/${s.id}/ai-proposal`),
    onSuccess: (p) => setProposta(p),
    // La consultazione ha appena detto se l'AI risponde: la riga in cima si allinea.
    onSettled: rileggiStatoAi,
  });
  const chiediRegola = useMutation({
    meta: { fatto: () => null },
    mutationFn: () => chiama<RegolaAi>('POST', `/feedback/${s.id}/ai-rule`),
    onSuccess: (r) => {
      setRegola(r);
      setTestoRegola(r.testo);
    },
    onSettled: rileggiStatoAi,
  });
  const aggiungi = useMutation({
    meta: { fatto: 'regola aggiunta in «Cosa dice il bot ai soci»' },
    mutationFn: () =>
      chiama<{ titolo: string }>('POST', `/feedback/${s.id}/rule`, {
        argomento: regola!.argomento,
        testo: testoRegola,
      }),
    onSuccess: (r) => {
      setRegolaFatta(r.titolo);
      rileggiIlBot();
    },
  });
  const risposta = useMutation({
    meta: { fatto: 'risposta mandata: arriva al socio nel bot' },
    mutationFn: () => chiama<Suggerimento>('POST', `/feedback/${s.id}/reply`, { text: bozza }),
    onSuccess: (aggiornato) => {
      setScrive(false);
      if (proposta && proposta.stato !== 'spenta') {
        // ✨ La regola si decide dopo: la segnalazione resta in pagina fino ad allora.
        trattieni(aggiornato);
        // Mandata la proposta così com'era: la regola c'è già, e non si spende di nuovo.
        if (usata !== null && usata.trim() === bozza.trim()) {
          setRegola({ stato: 'gia-c-e', argomento: null, titolo: proposta.fonte, testo: '' });
        } else chiediRegola.mutate();
      } else if (s.kind === 'senza_risposta' && !aiSpenta) {
        // 🤖 L'assistente non la sapeva: dopo la risposta, la regola perché la prossima volta la sappia.
        trattieni(aggiornato);
        chiediRegola.mutate();
      }
      rileggi();
    },
  });

  // 🤖 Sulla prenotazione senza esito la risposta sta nel calendario, non nelle regole: niente AI.
  const puoConsultare = s.kind !== 'suggerimento' && s.kind !== 'senza_esito';
  const dalBot = scrittoDalBot(s.kind);
  // 🤖 Se la prenotazione c'è, la risposta parte già scritta: basta rileggerla.
  const pronta =
    s.kind === 'senza_esito' && s.trovata?.stato === 'c-e'
      ? `Ciao ${nome}, la prenotazione è passata: ${s.text}. La trovi in «Prossime partite». Buona partita!`
      : '';
  const classe = s.kind.replace('_', '-');

  return (
    <article className={`voce ${classe}`}>
      <div className="testa">
        <span className={`tipo ${classe}`}>{ETICHETTE_TIPO_SUGGERIMENTO[s.kind]}</span>
        {dalBot ? (
          <span className="firma">
            🤖 Assistente digitale · per{' '}
            <Link to={`/soci?socio=${s.memberId}`} className="chi">
              {s.memberName}
            </Link>
          </span>
        ) : (
          <Link to={`/soci?socio=${s.memberId}`} className="chi">
            {s.memberName}
          </Link>
        )}
        <span className="tenue piccolo">
          {quando(s.createdAt)}
          {s.readAt && (dalBot ? ' · letta' : ' · letto')}
        </span>
        {/* ✉️ Quanto aspetta, e se gioca oggi: le urgenze si vedono senza che il socio debba dirle. */}
        {!s.reply && <span className="attesa">{daQuanto(s.createdAt)}</span>}
        {!s.reply && s.giocaOggi && <span className="gioca-oggi">gioca oggi alle {s.giocaOggi}</span>}
      </div>
      {s.filo && (
        <div className="filo tenue piccolo">
          <span>
            Il {s.filo.del}, {(s.memberName ?? '').split(' ')[0]}: «{s.filo.scritto}»
          </span>
          <span>Segreteria: «{s.filo.risposta}»</span>
        </div>
      )}
      {s.kind === 'senza_risposta' ? (
        <div className="filo piccolo">
          {[s.text, ...s.altre].map((d, i) => (
            <span key={i}>
              <span className="tenue">{i === 0 ? `${nome} ha chiesto all’assistente` : 'E poi'}:</span> «{d}»
            </span>
          ))}
          {s.botReply && (
            <span>
              <span className="tenue">L’assistente ha risposto:</span> «{s.botReply}»
            </span>
          )}
        </div>
      ) : s.kind === 'senza_esito' ? (
        <>
          <p className="testo">
            <strong>Prenotazione · {s.text}</strong>
          </p>
          {s.botReply && (
            <div className="filo piccolo">
              <span>
                <span className="tenue">L’assistente ha detto:</span> «{s.botReply}»
              </span>
            </div>
          )}
          {s.trovata && <NelGestionale s={s} />}
        </>
      ) : (
        <p className="testo">{s.text}</p>
      )}

      {s.reply && (
        <p className="risposta tenue piccolo">
          Risposto il {quando(s.reply.at)}
          {s.reply.by ? ` da ${s.reply.by}` : ''}: «{s.reply.text}»
        </p>
      )}

      {modificabile && !s.reply && !scrive && !proposta && !consulta.isPending && (
        <div className="azioni">
          <button
            type="button"
            onClick={() => {
              if (pronta && bozza === '') setBozza(pronta);
              setScrive(true);
            }}
          >
            Rispondi
          </button>
          {puoConsultare && (
            <button
              type="button"
              className="bordato"
              disabled={aiSpenta}
              title={aiSpenta ? 'L’AI non funziona: vedi la riga in cima' : undefined}
              onClick={() => consulta.mutate()}
            >
              ✨ Consulta AI
            </button>
          )}
          {!s.readAt && (
            <button
              type="button"
              className="bordato"
              disabled={letto.isPending}
              onClick={() => letto.mutate()}
            >
              {dalBot ? 'Segna come letta' : 'Segna come letto'}
            </button>
          )}
        </div>
      )}

      {consulta.isPending && (
        <div className="proposta-ai" aria-live="polite">
          <span className="etichetta">AI</span>
          <span className="scrive">sta scrivendo…</span>
        </div>
      )}

      {proposta && !s.reply && !scrive && (
        <div className={`proposta-ai${proposta.stato === 'proposta' ? '' : ' non-sa'}`} aria-live="polite">
          <span className="etichetta">Proposta dell’AI · non è partita</span>
          <p className="testo">{proposta.testo}</p>
          {proposta.fonte && (
            <span className="fonte">Da «{proposta.fonte}», in «Cosa dice il bot ai soci»</span>
          )}
          <div className="azioni">
            {proposta.stato === 'proposta' ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setBozza(proposta.testo);
                    setUsata(proposta.testo);
                    setScrive(true);
                  }}
                >
                  Usa questa risposta
                </button>
                <button type="button" className="bordato" onClick={() => setProposta(null)}>
                  Scarta
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setScrive(true)}>
                Rispondi a {nome}
              </button>
            )}
          </div>
        </div>
      )}

      {scrive && (
        <form
          className="rispondi"
          onSubmit={(e) => {
            e.preventDefault();
            risposta.mutate();
          }}
        >
          <label className="campo">
            <span>
              La risposta, per {nome}
              {usata !== null && <span className="tenue piccolo"> (dall’AI, si può correggere)</span>}
            </span>
            <textarea rows={3} maxLength={1000} value={bozza} onChange={(e) => setBozza(e.target.value)} />
          </label>
          <p className="tenue piccolo">
            Arriva al socio su Telegram, con il suo messaggio citato. Si manda una volta sola.
          </p>
          <div className="azioni">
            <button type="submit" disabled={risposta.isPending || bozza.trim() === ''}>
              {risposta.isPending ? 'Un attimo…' : 'Manda la risposta'}
            </button>
            <button type="button" className="bordato" onClick={() => setScrive(false)}>
              Lascia stare
            </button>
          </div>
        </form>
      )}

      {chiediRegola.isPending && (
        <div className="proposta-ai regola" aria-live="polite">
          <span className="etichetta">La regola che propone l’AI</span>
          <span className="scrive">sta scrivendo…</span>
        </div>
      )}

      {regola && regolaFatta === null && regola.stato === 'proposta' && (
        <div className="proposta-ai regola">
          <span className="etichetta">La regola che propone l’AI</span>
          <span className="tenue piccolo">
            Per la prossima volta, per tutti i soci. Argomento: «{regola.titolo}».
          </span>
          <label className="campo">
            <textarea
              aria-label="Testo della regola"
              rows={3}
              maxLength={600}
              value={testoRegola}
              onChange={(e) => setTestoRegola(e.target.value)}
            />
          </label>
          <div className="azioni">
            <button
              type="button"
              disabled={aggiungi.isPending || testoRegola.trim() === ''}
              onClick={() => aggiungi.mutate()}
            >
              {aggiungi.isPending ? 'Un attimo…' : 'Aggiungi la regola'}
            </button>
            <button type="button" className="bordato" onClick={() => rilascia(s.id)}>
              No, valeva solo per {nome}
            </button>
          </div>
        </div>
      )}
      {regola && regola.stato === 'gia-c-e' && (
        <p className="tenue piccolo">
          La regola c’è già in «{regola.titolo ?? 'Cosa dice il bot ai soci'}»: non serve aggiungerla.
        </p>
      )}
      {regola && (regola.stato === 'spenta' || regola.stato === 'non-risponde') && (
        <p className="tenue piccolo">{regola.testo}</p>
      )}
      {regolaFatta !== null && (
        <p className="fatto-regola">
          ✓ Regola aggiunta in «{regolaFatta}». Da adesso il bot risponde da solo.
        </p>
      )}
      <Guasto
        errore={letto.error ?? risposta.error ?? consulta.error ?? chiediRegola.error ?? aggiungi.error}
      />
    </article>
  );
}

/**
 * 🤖 Per la prenotazione senza esito: se nel gestionale c'è, guardando adesso
 * (scelta 3 A). Il gestionale la ritrova con la chiave con cui il bot l'aveva
 * chiesta.
 */
function NelGestionale({ s }: { s: Suggerimento }): React.JSX.Element {
  const t = s.trovata!;
  const giorno = s.richiesta?.date;
  const dove = t.bookingId
    ? `/calendario?data=${giorno}&scheda=${t.bookingId}`
    : giorno
      ? `/calendario?data=${giorno}`
      : '/calendario';
  return (
    <div className={`nel-gestionale ${t.stato}`}>
      <span className="tenue piccolo">Nel gestionale, adesso</span>
      <span className="stato">
        {t.stato === 'c-e'
          ? `✅ La prenotazione c’è: è nata alle ${t.natoAlle ?? '—'}.`
          : t.stato === 'annullata'
            ? `⚠️ C’era, ma è stata annullata (era nata alle ${t.natoAlle ?? '—'}).`
            : '❌ La prenotazione non c’è.'}
      </span>
      <span>
        <Link to={dove} className="apri">
          Apri nel calendario
        </Link>
      </span>
    </div>
  );
}
